// Money in and out: the VegaPart subscription, the shop's Stripe account, invoice payments
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { date, dateTime, money } from './format';
import { useMe } from './session';
import { confirmDelete, FormModal, Loading, Modal, Table, toast, toastError, type Field } from './ui';
import type { WorkOrder } from './types';

const go = (url: string) => { window.location.href = url; };

/** Thin line at the top of the app: trial ending, card failed, or read-only */
export function BillingBanner() {
  const { t } = useTranslation();
  const me = useMe();
  const b = me.billing;
  if (!b || (b.access === 'full' && !b.warning)) return null;
  const text = b.access === 'read_only' ? t('bill.banner_read_only')
    : b.warning === 'past_due' ? t('bill.banner_past_due')
    : t('bill.banner_trial', { date: date(b.trial_ends_at) });
  return (
    <div className={'bill-banner no-print' + (b.access === 'read_only' ? ' bad' : '')}>
      <span>{text}</span>
      {me.isAdmin ? <Link className="btn sm" to="/settings#billing">{t('bill.subscribe')}</Link> : <span className="sub">{t('bill.ask_admin')}</span>}
    </div>
  );
}

interface BillingInfo {
  access: 'full' | 'read_only'; warning: string | null; status: string; trial_ends_at: string | null; current_period_end: string | null;
  exempt: boolean; has_subscription: boolean; modules: string[]; required: boolean; enabled: boolean;
  prices: { key: string; amount_cents: number | null; currency: string; interval: string | null }[];
  lines: { key: string; quantity: number; unit_amount_cents: number | null; amount_cents: number | null; currency: string | null }[];
  usage: { users: number; included_users: number; vehicles: number; included_vehicles: number };
}

/** Settings → VegaPart subscription */
export function BillingCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ['billing'], queryFn: () => api.get<BillingInfo>('/billing') });
  useEffect(() => {
    const r = params.get('billing');
    if (!r) return;
    if (r === 'success') toast(t('bill.thanks'));
    // Stripe's webhook may land a second after the redirect
    const timer = setTimeout(() => { void qc.invalidateQueries({ queryKey: ['billing'] }); void qc.invalidateQueries({ queryKey: ['me'] }); }, 2000);
    params.delete('billing'); setParams(params, { replace: true });
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!q.data) return <Loading />;
  const b = q.data;
  const open = async (path: string) => {
    setBusy(true);
    try { go((await api.post<{ url: string }>(path)).url); } catch (e) { toastError(e); setBusy(false); }
  };
  const total = b.lines.reduce((s, l) => s + (l.amount_cents ?? 0), 0);
  const cur = b.prices[0]?.currency?.toUpperCase();
  const lineName = (k: string) => (k === 'shop_extra_user' ? t('bill.extra_users') : k === 'fleet_extra_vehicle' ? t('bill.extra_vehicles') : t(`kind.${k}.title`));
  return (
    <div className="stack">
      <div className="kv">
        <div><span className="sub">{t('bill.status')}</span>
          <b className={b.access === 'read_only' ? 'bad-text' : ''}>{b.exempt ? t('bill.exempt') : t('bill.st.' + (b.has_subscription ? b.status : b.access === 'read_only' ? 'ended' : 'trialing'))}</b></div>
        {!b.has_subscription && !b.exempt && b.trial_ends_at && <div><span className="sub">{t('bill.trial_until')}</span><b>{date(b.trial_ends_at)}</b></div>}
        {b.has_subscription && b.current_period_end && <div><span className="sub">{t('bill.next_charge')}</span><b>{date(b.current_period_end)}</b></div>}
        {total > 0 && <div><span className="sub">{t('bill.monthly')}</span><b>{money(total, cur)}</b></div>}
      </div>
      <Table rows={b.lines} columns={[
        { label: t('set.packages'), render: l => lineName(l.key) },
        { label: t('wo.qty'), render: l => l.quantity, num: true },
        { label: t('bill.price'), render: l => (l.unit_amount_cents != null ? money(l.unit_amount_cents, l.currency?.toUpperCase()) : '—'), num: true },
        { label: t('bill.monthly'), render: l => (l.amount_cents != null ? money(l.amount_cents, l.currency?.toUpperCase()) : '—'), num: true }
      ]} />
      <p className="sub" style={{ margin: 0 }}>
        {b.modules.includes('shop') && t('bill.usage_users', { n: b.usage.users, included: b.usage.included_users })}
        {b.modules.includes('shop') && b.modules.includes('fleet') && ' · '}
        {b.modules.includes('fleet') && t('bill.usage_vehicles', { n: b.usage.vehicles, included: b.usage.included_vehicles })}
      </p>
      {!b.enabled ? <p className="sub" style={{ margin: 0 }}>{t('bill.not_configured')}</p> : !b.exempt && (
        <div className="toolbar">
          {b.has_subscription
            ? <button className="btn" disabled={busy} onClick={() => void open('/billing/portal')}>{t('bill.manage')}</button>
            : <button className="btn primary" disabled={busy} onClick={() => void open('/billing/checkout')}>{t('bill.subscribe')}</button>}
        </div>
      )}
      <p className="sub" style={{ margin: 0 }}>{t('bill.hint')}</p>
    </div>
  );
}

interface ConnectInfo { connected: boolean; charges_enabled: boolean; details_submitted: boolean; platform_fee_bps: number }

/** Settings → the shop's own Stripe account, where customers' payments land */
export function StripeConnectCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ['stripe-connect'], queryFn: () => api.get<ConnectInfo>('/payments/connect') });
  useEffect(() => {
    if (!params.get('stripe')) return;
    // Back from Stripe onboarding: ask Stripe directly instead of waiting for the webhook
    void api.post<ConnectInfo>('/payments/connect/refresh').then(d => qc.setQueryData(['stripe-connect'], d)).catch(() => {});
    params.delete('stripe'); setParams(params, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (q.isError) return <p className="sub" style={{ margin: 0 }}>{t('bill.not_configured')}</p>;
  if (!q.data) return <Loading />;
  const c = q.data;
  const start = async () => {
    setBusy(true);
    try { go((await api.post<{ url: string }>('/payments/connect')).url); } catch (e) { toastError(e); setBusy(false); }
  };
  return (
    <div className="stack">
      <div>
        {c.charges_enabled ? <span className="badge b-ok">✓ {t('pay.connected')}</span>
          : c.connected ? <span className="badge b-due_soon">{t('pay.onboarding_unfinished')}</span>
          : <span className="badge">{t('pay.not_connected')}</span>}
      </div>
      <p className="sub" style={{ margin: 0 }}>{t('pay.connect_hint')}{c.platform_fee_bps > 0 && ' ' + t('pay.fee_note', { pct: (c.platform_fee_bps / 100).toFixed(2) })}</p>
      <div className="toolbar">
        {!c.charges_enabled && <button className="btn primary" disabled={busy} onClick={() => void start()}>{c.connected ? t('pay.continue_onboarding') : t('pay.connect')}</button>}
        {c.charges_enabled && <a className="btn" href="https://dashboard.stripe.com" target="_blank" rel="noreferrer">{t('pay.open_stripe')}</a>}
      </div>
    </div>
  );
}

const METHODS = ['cash', 'check', 'card', 'ach', 'other'] as const;

/** Work order sidebar: what was paid, what is owed, and how to collect it */
export function WoPayments({ wo, onChanged }: { wo: WorkOrder; onChanged: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const [dialog, setDialog] = useState<'send' | 'record' | null>(null);
  const ready = ['completed', 'invoiced', 'paid'].includes(wo.status);
  if (!me.isStaff) return null;
  return (
    <div className="card stack">
      <div className="head" style={{ margin: 0 }}><h2 style={{ margin: 0 }}>{t('pay.title')}</h2></div>
      <div className="totals" style={{ width: 'auto', margin: 0 }}>
        <div><span>{t('pay.paid')}</span><span>{money(wo.paid_cents)}</span></div>
        <div className="grand"><span>{t('pay.balance')}</span><span>{money(wo.balance_cents)}</span></div>
      </div>
      {wo.payments.length > 0 && <ul className="pay-list">{wo.payments.map(p => (
        <li key={p.id} className={p.status === 'refunded' ? 'refunded' : ''}>
          <div><b>{money(p.amount_cents)}</b> · {t('pay.m.' + p.method)}{p.status === 'refunded' && <> · {t('pay.refunded')}</>}
            <div className="sub">{dateTime(p.created_at)}{p.note && ` · ${p.note}`}</div></div>
          {me.isAdmin && !p.online && <button className="btn sm" title={t('common.delete')} onClick={async () => {
            if (!confirmDelete()) return;
            try { await api.del(`/work-orders/${wo.id}/payments/${p.id}`); onChanged(); } catch (e) { toastError(e); }
          }}>✕</button>}
        </li>
      ))}</ul>}
      {!ready ? <p className="sub" style={{ margin: 0 }}>{t('pay.finish_first')}</p> : wo.balance_cents > 0 && (
        <div className="toolbar">
          {me.has('shop') && <button className="btn primary" onClick={() => setDialog('send')}>{t('pay.send_invoice')}</button>}
          <button className="btn" onClick={() => setDialog('record')}>{t('pay.record')}</button>
        </div>
      )}
      {dialog === 'send' && <SendInvoiceDialog wo={wo} onClose={() => setDialog(null)} />}
      {dialog === 'record' && <FormModal title={t('pay.record')} onClose={() => setDialog(null)}
        values={{ amount_cents: wo.balance_cents, method: 'cash' }}
        fields={[
          { name: 'amount_cents', label: t('pay.amount'), type: 'money', required: true },
          { name: 'method', label: t('pay.method'), type: 'select', blank: false, options: METHODS.map(m => [m, t('pay.m.' + m)]) },
          { name: 'note', label: t('pay.note'), full: true, placeholder: t('pay.note_hint') }
        ] satisfies Field[]}
        onSubmit={async v => { await api.post(`/work-orders/${wo.id}/payments`, v); toast(t('common.saved')); onChanged(); }} />}
    </div>
  );
}

function SendInvoiceDialog({ wo, onClose }: { wo: WorkOrder; onClose: () => void }) {
  const { t } = useTranslation();
  const connect = useQuery({ queryKey: ['stripe-connect'], queryFn: () => api.get<ConnectInfo>('/payments/connect').catch(() => null) });
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const make = async (send: boolean) => {
    setBusy(true);
    try {
      const r = await api.post<{ url: string; sent_to: string | null }>(`/work-orders/${wo.id}/payment-link`, { send, ...(email.trim() ? { email: email.trim() } : {}) });
      setUrl(r.url);
      if (send) { toast(t('pay.sent', { email: r.sent_to })); onClose(); }
    } catch (e) { toastError(e); } finally { setBusy(false); }
  };
  const canTakeCards = connect.data?.charges_enabled;
  return (
    <Modal title={t('pay.send_invoice')} onClose={onClose}>
      <div className="stack">
        {connect.data !== undefined && !canTakeCards && <div className="note warn">{t('pay.connect_first')} <Link to="/settings#payments">{t('nav.settings')} →</Link></div>}
        <p className="sub" style={{ margin: 0 }}>{t('pay.send_hint', { amount: money(wo.balance_cents) })}</p>
        <label className="f">{t('pay.email')}<input type="email" value={email} placeholder={t('pay.email_default')} onChange={e => setEmail(e.target.value)} /></label>
        {url && <div className="copy-row"><input readOnly value={url} onFocus={e => e.target.select()} />
          <button className="btn sm" onClick={() => { void navigator.clipboard?.writeText(url); toast(t('pay.copied')); }}>{t('pay.copy')}</button></div>}
        <div className="actions">
          <button className="btn" disabled={busy} onClick={() => void make(false)}>{t('pay.get_link')}</button>
          <button className="btn primary" disabled={busy} onClick={() => void make(true)}>{t('pay.email_it')}</button>
        </div>
      </div>
    </Modal>
  );
}
